import { useCallback, useEffect, useRef, useState } from "react";

export interface FetchState<T> {
  data: T | null;
  error: Error | null;
  loading: boolean;
  refresh: () => Promise<void>;
}

export function useFetch<T>(
  loader: () => Promise<T>,
  deps: ReadonlyArray<unknown> = [],
): FetchState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);
  const cancelRef = useRef(0);

  // Hold the latest loader without forcing the effect to re-run on every render.
  const loaderRef = useRef(loader);
  loaderRef.current = loader;

  const run = useCallback(async () => {
    const myToken = ++cancelRef.current;
    setLoading(true);
    setError(null);
    try {
      const result = await loaderRef.current();
      if (cancelRef.current === myToken) setData(result);
    } catch (err) {
      if (cancelRef.current === myToken) {
        setError(err instanceof Error ? err : new Error(String(err)));
      }
    } finally {
      if (cancelRef.current === myToken) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void run();
    return () => {
      cancelRef.current += 1;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, error, loading, refresh: run };
}
